import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, User, Mail, DollarSign, Calendar, ChevronDown, ChevronUp, Brain } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Loading } from '../../components/ui/Loading';
import { usePayment } from '../../hooks/usePayments';
import { usePaymentIntelligence, useRiskRecommendations } from '../../hooks/useIntelligence';
import { LossProbabilityBadge, RiskBreakdown, AiRecommendationPanel } from '../../components/intelligence';
import { formatCurrency, formatDate, formatRelativeTime } from '../../lib/utils';

const PaymentDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { data: payment, isLoading: paymentLoading } = usePayment(id!);
    const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set());
    const [showAiRecs, setShowAiRecs] = useState(false);

    // Fetch payment intelligence (loss probability)
    const { data: intelligence, isLoading: intelligenceLoading, error: intelligenceError } = usePaymentIntelligence(id || null);

    // Fetch AI recommendations (only if user clicks)
    const firstRiskId = payment?.risks?.[0]?.id;
    const { data: recommendations, isLoading: recsLoading, error: recsError } = useRiskRecommendations(firstRiskId || null, showAiRecs);

    const toggleEvent = (eventId: number) => {
        setExpandedEvents((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(eventId)) {
                newSet.delete(eventId);
            } else {
                newSet.add(eventId);
            }
            return newSet;
        });
    };

    if (paymentLoading) {
        return <Loading fullScreen message="Loading payment details..." />;
    }

    if (!payment) {
        return (
            <div className="text-center py-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Not Found</h2>
                <p className="text-gray-600 mb-6">The payment you're looking for doesn't exist.</p>
                <Link to="/payments" className="btn-primary">
                    Back to Payments
                </Link>
            </div>
        );
    }

    return (
        <div className="space-y-6 overflow-x-hidden">
            {/* Back Button */}
            <Link to="/payments" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900">
                <ArrowLeft className="w-5 h-5" />
                Back to Payments
            </Link>

            {/* Header */}
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Details</h1>
                    <p className="text-gray-600 font-mono">{payment.id}</p>
                </div>
                {intelligence && intelligence.scores && (
                    <div>
                        <LossProbabilityBadge
                            probability={Math.round(intelligence.scores.loss_probability)}
                            riskLevel={intelligence.scores.risk_level}
                            confidence={intelligence.scores.confidence}
                            size="lg"
                        />
                    </div>
                )}
            </div>

            {/* Main Info Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 overflow-hidden">
                {/* Payment Info */}
                <Card className="lg:col-span-2 overflow-hidden">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h3>
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label className="text-sm font-medium text-gray-500">Amount</label>
                            <div className="flex items-center gap-2 mt-1">
                                <DollarSign className="w-5 h-5 text-gray-400" />
                                <span className="text-xl font-bold text-gray-900">
                                    {formatCurrency(payment.expected_amount, payment.currency)}
                                </span>
                            </div>
                        </div>

                        <div>
                            <label className="text-sm font-medium text-gray-500">Status</label>
                            <div className="mt-1">
                                <Badge variant="status" status={payment.status}>
                                    {payment.status}
                                </Badge>
                            </div>
                        </div>

                        <div>
                            <label className="text-sm font-medium text-gray-500">Provider</label>
                            <div className="mt-1">
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 capitalize">
                                    {payment.provider}
                                </span>
                            </div>
                        </div>

                        <div>
                            <label className="text-sm font-medium text-gray-500">Created</label>
                            <div className="flex items-center gap-2 mt-1">
                                <Calendar className="w-5 h-5 text-gray-400" />
                                <span className="text-sm text-gray-900">{formatDate(payment.created_at)}</span>
                            </div>
                        </div>

                        <div>
                            <label className="text-sm font-medium text-gray-500">User ID</label>
                            <div className="flex items-center gap-2 mt-1">
                                <User className="w-5 h-5 text-gray-400" />
                                <span className="text-sm text-gray-900">{payment.external_user_id}</span>
                            </div>
                        </div>

                        {payment.metadata?.customer_email && (
                            <div>
                                <label className="text-sm font-medium text-gray-500">Customer Email</label>
                                <div className="flex items-center gap-2 mt-1">
                                    <Mail className="w-5 h-5 text-gray-400" />
                                    <span className="text-sm text-gray-900">{payment.metadata.customer_email}</span>
                                </div>
                            </div>
                        )}

                        <div className="col-span-2">
                            <label className="text-sm font-medium text-gray-500">Order ID</label>
                            <div className="mt-1">
                                <span className="text-sm font-mono text-gray-900">{payment.external_order_id}</span>
                            </div>
                        </div>
                    </div>

                    {/* Metadata */}
                    {payment.metadata && Object.keys(payment.metadata).length > 0 && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <h4 className="text-sm font-semibold text-gray-900 mb-3">Metadata</h4>
                            <div className="bg-gray-50 rounded-lg border border-gray-200" style={{ maxHeight: '400px', overflowX: 'auto', overflowY: 'auto', maxWidth: '100%' }}>
                                <pre className="text-xs text-gray-700 font-mono p-4" style={{ margin: 0, whiteSpace: 'pre', wordWrap: 'normal', overflowWrap: 'normal' }}>
                                    {JSON.stringify(payment.metadata, null, 2)}
                                </pre>
                            </div>
                        </div>
                    )}
                </Card>

                {/* Risks */}
                <Card className="overflow-hidden">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Flags</h3>
                    {payment.risks && payment.risks.length > 0 ? (
                        <div className="space-y-4">
                            {payment.risks.map((risk) => (
                                <div
                                    key={risk.id}
                                    className="p-4 border border-gray-200 rounded-lg"
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <Badge variant="severity" severity={risk.severity}>
                                            {risk.severity}
                                        </Badge>
                                        <Badge>{risk.resolved ? 'Resolved' : 'Pending'}</Badge>
                                    </div>
                                    <p className="text-sm font-medium text-gray-900 mb-1">{risk.type.replace(/_/g, ' ')}</p>
                                    <p className="text-xs text-gray-600 mb-1">{risk.description}</p>
                                    <div className="flex items-center justify-between mt-2">
                                        <p className="text-xs text-gray-400">{formatRelativeTime(risk.detected_at)}</p>
                                        {risk.estimated_loss > 0 && (
                                            <p className="text-xs font-semibold text-red-600">
                                                Loss: {formatCurrency(risk.estimated_loss)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-6">
                            <div className="w-12 h-12 rounded-full bg-green-100 mx-auto mb-3 flex items-center justify-center">
                                <span className="text-2xl">✓</span>
                            </div>
                            <p className="text-sm text-gray-600">No risks detected</p>
                        </div>
                    )}
                </Card>
            </div>

            {/* Risk Breakdown & AI Recommendations */}
            {intelligence && intelligence.breakdown && intelligence.top_factors ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Risk Factor Breakdown */}
                    <Card>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Analysis</h3>
                        <RiskBreakdown
                            breakdown={intelligence.breakdown}
                            topFactors={intelligence.top_factors}
                        />
                    </Card>

                    {/* Intelligence Features */}
                    <Card>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Intelligence Features</h3>
                        <div className="space-y-4">
                            {/* Timing Section */}
                            {intelligence.features?.timing && (
                                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                                    <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                                        <Calendar className="w-4 h-4" />
                                        Timing
                                    </h4>
                                    <div className="grid grid-cols-2 gap-3 text-xs">
                                        <div className="flex justify-between items-center">
                                            <span className="text-blue-700">Hour:</span>
                                            <span className="font-semibold text-blue-900">{intelligence.features.timing.hour}:00</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-blue-700">Day:</span>
                                            <span className="font-semibold text-blue-900">
                                                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][intelligence.features.timing.day_of_week]}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-blue-700">Weekend:</span>
                                            <span className={`font-semibold ${intelligence.features.timing.is_weekend ? 'text-orange-600' : 'text-green-600'}`}>
                                                {intelligence.features.timing.is_weekend ? '✓ Yes' : '✗ No'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-blue-700">Night:</span>
                                            <span className={`font-semibold ${intelligence.features.timing.is_night ? 'text-purple-600' : 'text-green-600'}`}>
                                                {intelligence.features.timing.is_night ? '✓ Yes' : '✗ No'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Latency Section */}
                            {intelligence.features?.latency && (
                                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                                    <h4 className="text-sm font-semibold text-yellow-900 mb-3 flex items-center gap-2">
                                        ⚡ Latency
                                    </h4>
                                    <div className="space-y-2 text-xs">
                                        {intelligence.features.latency.total_ms !== null && (
                                            <div className="flex justify-between items-center p-2 bg-yellow-100 rounded">
                                                <span className="text-yellow-700 font-medium">Total Duration:</span>
                                                <span className="font-bold text-yellow-900">
                                                    {(intelligence.features.latency.total_ms / 1000).toFixed(2)}s
                                                </span>
                                            </div>
                                        )}
                                        {intelligence.features.latency.sdk_ms !== null && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-yellow-700">SDK → Provider:</span>
                                                <span className="font-semibold text-yellow-900">
                                                    {(intelligence.features.latency.sdk_ms / 1000).toFixed(2)}s
                                                </span>
                                            </div>
                                        )}
                                        {intelligence.features.latency.provider_ms !== null && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-yellow-700">Provider Processing:</span>
                                                <span className="font-semibold text-yellow-900">
                                                    {(intelligence.features.latency.provider_ms / 1000).toFixed(2)}s
                                                </span>
                                            </div>
                                        )}
                                        {intelligence.features.latency.webhook_ms !== null && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-yellow-700">Webhook Response:</span>
                                                <span className="font-semibold text-yellow-900">
                                                    {(intelligence.features.latency.webhook_ms / 1000).toFixed(2)}s
                                                </span>
                                            </div>
                                        )}
                                        {intelligence.features.latency.revenue_ms !== null && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-yellow-700">Revenue Recording:</span>
                                                <span className="font-semibold text-yellow-900">
                                                    {(intelligence.features.latency.revenue_ms / 1000).toFixed(2)}s
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Behavior Section */}
                            {intelligence.features?.behavior && (
                                <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                                    <h4 className="text-sm font-semibold text-purple-900 mb-3 flex items-center gap-2">
                                        🔄 Behavior
                                    </h4>
                                    <div className="grid grid-cols-2 gap-3 text-xs">
                                        <div className="flex justify-between items-center">
                                            <span className="text-purple-700">Retry Count:</span>
                                            <span className="font-semibold text-purple-900">{intelligence.features.behavior.retry_count || 0}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-purple-700">Total Events:</span>
                                            <span className="font-semibold text-purple-900">{intelligence.features.behavior.event_count || 0}</span>
                                        </div>
                                        {intelligence.features.behavior.state_entropy !== null && intelligence.features.behavior.state_entropy !== undefined && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-purple-700">State Entropy:</span>
                                                <span className="font-semibold text-purple-900">
                                                    {typeof intelligence.features.behavior.state_entropy === 'number'
                                                        ? intelligence.features.behavior.state_entropy.toFixed(2)
                                                        : intelligence.features.behavior.state_entropy}
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex justify-between items-center">
                                            <span className="text-purple-700">Has Issues:</span>
                                            <span className={`font-semibold ${intelligence.features.behavior.has_issues ? 'text-red-600' : 'text-green-600'}`}>
                                                {intelligence.features.behavior.has_issues ? '⚠️ Yes' : '✓ No'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Context Section */}
                            {intelligence.features?.context && (
                                <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                                    <h4 className="text-sm font-semibold text-green-900 mb-3 flex items-center gap-2">
                                        📊 Historical Context
                                    </h4>
                                    <div className="space-y-2 text-xs">
                                        {intelligence.features.context.org_loss_rate_7d !== null && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-green-700">Org Loss Rate (7d):</span>
                                                <span className="font-semibold text-green-900">
                                                    {intelligence.features.context.org_loss_rate_7d.toFixed(1)}%
                                                </span>
                                            </div>
                                        )}
                                        {intelligence.features.context.org_loss_rate_30d !== null && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-green-700">Org Loss Rate (30d):</span>
                                                <span className="font-semibold text-green-900">
                                                    {intelligence.features.context.org_loss_rate_30d.toFixed(1)}%
                                                </span>
                                            </div>
                                        )}
                                        {intelligence.features.context.provider_loss_rate !== null && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-green-700">Provider Loss Rate:</span>
                                                <span className="font-semibold text-green-900">
                                                    {intelligence.features.context.provider_loss_rate.toFixed(1)}%
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Matched Patterns */}
                            {intelligence.matched_patterns && intelligence.matched_patterns.length > 0 && (
                                <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                                    <h4 className="text-sm font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                                        🏷️ Matched Patterns
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {intelligence.matched_patterns.map((pattern: string) => (
                                            <span
                                                key={pattern}
                                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-200"
                                            >
                                                {pattern.replace(/_/g, ' ')}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* No Features Message */}
                            {!intelligence.features && (
                                <div className="text-center py-8">
                                    <div className="w-12 h-12 rounded-full bg-gray-100 mx-auto mb-3 flex items-center justify-center">
                                        <Brain className="w-6 h-6 text-gray-400" />
                                    </div>
                                    <p className="text-sm text-gray-500">Intelligence data is being processed...</p>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
            ) : intelligenceLoading ? (
                <Card>
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-3 text-sm text-gray-600">Loading intelligence data...</span>
                    </div>
                </Card>
            ) : intelligenceError ? (
                <Card>
                    <div className="text-center py-8">
                        <div className="w-12 h-12 rounded-full bg-gray-100 mx-auto mb-3 flex items-center justify-center">
                            <Brain className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-sm text-gray-600">
                            {(intelligenceError as any)?.response?.data?.error?.code === 'INTELLIGENCE_NOT_READY'
                                ? 'Intelligence analysis in progress'
                                : (intelligenceError as any)?.response?.data?.error?.code === 'PAYMENT_NOT_FOUND'
                                    ? 'Payment not found for intelligence analysis'
                                    : 'No intelligence data available yet'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                            {(intelligenceError as any)?.response?.data?.error?.code === 'INTELLIGENCE_NOT_READY'
                                ? 'Intelligence will be calculated once this payment reaches a terminal state'
                                : 'Intelligence analysis will be available once this payment is processed'}
                        </p>
                    </div>
                </Card>
            ) : null}

            {/* AI Recommendations */}
            {payment.risks && payment.risks.length > 0 && (
                <Card>
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">AI Recommendations</h3>
                        {!showAiRecs && (
                            <button
                                onClick={() => setShowAiRecs(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <Brain size={16} />
                                Get AI Recommendations
                            </button>
                        )}
                    </div>

                    {showAiRecs && (
                        <>
                            {recsLoading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                    <span className="ml-3 text-sm text-gray-600">Analyzing risks...</span>
                                </div>
                            ) : (recsError as any)?.response?.data?.error?.code === 'FEATURE_NOT_AVAILABLE' ? (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <p className="text-sm text-yellow-800 font-medium">AI Recommendations are not available in your plan</p>
                                    <p className="text-xs text-yellow-700 mt-1">
                                        Upgrade to Pro or Enterprise to unlock AI-powered recommendations.
                                    </p>
                                    <Link
                                        to="/pricing"
                                        className="inline-block mt-3 px-4 py-2 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700"
                                    >
                                        View Plans
                                    </Link>
                                </div>
                            ) : recommendations ? (
                                <AiRecommendationPanel
                                    recommendations={recommendations.recommendations}
                                    rootCause={recommendations.root_cause.hypothesis}
                                    provider={recommendations.provider}
                                    cached={recommendations.root_cause.cached}
                                />
                            ) : recsError ? (
                                <div className="text-center py-8">
                                    <div className="w-12 h-12 rounded-full bg-gray-100 mx-auto mb-3 flex items-center justify-center">
                                        <Brain className="w-6 h-6 text-gray-400" />
                                    </div>
                                    <p className="text-sm text-gray-600">No AI recommendations available</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Recommendations will be generated based on detected risk patterns
                                    </p>
                                </div>
                            ) : null}
                        </>
                    )}
                </Card>
            )}

            {/* Timeline */}
            <Card className="overflow-hidden">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Payment Timeline</h3>
                {payment.timeline && payment.timeline.length > 0 ? (
                    <div className="space-y-6 overflow-hidden">
                        {payment.timeline.map((event, index, array) => {
                            const isExpanded = expandedEvents.has(event.id);
                            const hasMetadata = event.metadata && Object.keys(event.metadata).length > 0;

                            return (
                                <div key={event.id} className="relative pl-8 min-w-0 overflow-hidden">
                                    {index !== array.length - 1 && (
                                        <div className="absolute left-2 top-6 w-0.5 h-full bg-gray-200" />
                                    )}
                                    <div className="absolute left-0 top-1 w-4 h-4 rounded-full bg-primary-600 border-2 border-white" />

                                    <div className="flex items-start justify-between min-w-0">
                                        <div className="flex-1 min-w-0 overflow-hidden">
                                            <p className="font-medium text-gray-900">{event.state_label}</p>
                                            <p className="text-sm text-gray-600 mt-1">Source: {event.source_label}</p>

                                            <div className="flex items-center gap-2 mt-2">
                                                {event.is_successful && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                        Successful
                                                    </span>
                                                )}
                                                {event.is_failed && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                                        Failed
                                                    </span>
                                                )}
                                                {event.is_terminal && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                        Terminal
                                                    </span>
                                                )}
                                            </div>

                                            {/* Metadata Toggle Button */}
                                            {hasMetadata && (
                                                <button
                                                    onClick={() => toggleEvent(event.id)}
                                                    className="mt-3 flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 transition-colors"
                                                >
                                                    {isExpanded ? (
                                                        <>
                                                            <ChevronUp className="w-4 h-4" />
                                                            Hide Metadata
                                                        </>
                                                    ) : (
                                                        <>
                                                            <ChevronDown className="w-4 h-4" />
                                                            Show Metadata ({Object.keys(event.metadata).length} fields)
                                                        </>
                                                    )}
                                                </button>
                                            )}

                                            {/* Metadata Content - JSON Format */}
                                            {hasMetadata && isExpanded && (
                                                <div className="mt-3 bg-gray-50 rounded-lg border border-gray-200" style={{ maxHeight: '400px', overflowX: 'auto', overflowY: 'auto', maxWidth: '100%' }}>
                                                    <pre className="text-xs text-gray-700 font-mono p-4" style={{ margin: 0, whiteSpace: 'pre', wordWrap: 'normal', overflowWrap: 'normal' }}>
                                                        {JSON.stringify(event.metadata, null, 2)}
                                                    </pre>
                                                </div>
                                            )}
                                        </div>

                                        <div className="text-right ml-4">
                                            <Badge>{event.state}</Badge>
                                            <p className="text-xs text-gray-500 mt-1 whitespace-nowrap">
                                                {formatDate(event.occurred_at)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-6">
                        <p className="text-sm text-gray-600">No timeline events found</p>
                    </div>
                )}
            </Card>

        </div>
    );
};

export default PaymentDetail;
